<script setup lang="ts">
import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import { useFlightsFiltersStore } from '@apps/main/new-desktop/core/stores/filters/flightsFiltersStore';
import {
  isAlternativeFlightSegment,
  isAvailableFlightSegment,
  isUnavailableFlightSegment,
} from '@apps/main/new-desktop/pages/tripOverview/models';

import type { TransportType } from '@libs/trip-planner-interfaces';

import type { ItineraryFlightSegment } from '../../../models';
import {
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
} from '../../../stores';

import AlternativeFlightSegment from './AlternativeFlightSegment.vue';
import AvailableFlightSegment from './AvailableFlightSegment.vue';
import UnavailableFlightSegment from './UnavailableFlightSegment.vue';

// NOTE: Important:
// * this component is smart
// * keep child components of this one dumb
// * every action in this component must be guarded by print mode flag

interface ItineraryFlightSegmentProps {
  segment: ItineraryFlightSegment;
  printMode: boolean;
}

const props = defineProps<ItineraryFlightSegmentProps>();

const flightsFiltersStore = useFlightsFiltersStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();

function onShowDetails(): void {
  if (props.printMode) {
    return;
  }

  tripOverviewPageStore.showFlightDetailsModal(props.segment.ingredientId);
}

function onShowMoreFilters(): void {
  if (props.printMode) {
    return;
  }
  flightsFiltersStore.setAllowedFlightIngredientIds([
    props.segment.ingredientId,
  ]);
  tripOverviewPanelsStore.openFlightsFiltersPanel();
}

function onSuggestionTypeSelected(suggestionType: SuggestionType): void {
  if (props.printMode) {
    return;
  }
  tripOverviewPageStore.updateSuggestionType(
    props.segment.ingredientId,
    suggestionType
  );
}

function onSelectTransport(transportType: TransportType): void {
  if (props.printMode) {
    return;
  }

  tripOverviewPageStore.selectTransport(props.segment, transportType);
}

function onRemoveFilter(tagId: string): void {
  if (props.printMode) {
    return;
  }

  flightsFiltersStore.removeFilter(tagId, props.segment.ingredientId);
}
</script>

<template>
  <AvailableFlightSegment
    v-if="isAvailableFlightSegment(props.segment)"
    :segment="props.segment"
    :print-mode="props.printMode"
    @show-details="onShowDetails"
    @show-more-filters="onShowMoreFilters"
    @suggestion-type-selected="onSuggestionTypeSelected($event)"
    @select-transport="onSelectTransport($event)"
    @remove-filter="onRemoveFilter($event)"
  />

  <AlternativeFlightSegment
    v-if="isAlternativeFlightSegment(props.segment)"
    :segment="props.segment"
    @show-details="
      tripOverviewPageStore.showFlightDetailsModal(props.segment.ingredientId)
    "
    @confirm-alternative="
      tripOverviewPageStore.confirmAlternativeFlight(props.segment.ingredientId)
    "
    @remove-filter="onRemoveFilter($event)"
    @show-more-filters="onShowMoreFilters"
  />

  <UnavailableFlightSegment
    v-if="isUnavailableFlightSegment(props.segment)"
    :segment="props.segment"
    @remove-filter="onRemoveFilter($event)"
    @show-more-filters="onShowMoreFilters"
  />
</template>
